import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';

const labels = {
  ready_for_operator_review: 'Review nodig',
  approved_for_generation: 'Code generatie',
  generation_failed: 'Generatie mislukt',
  patch_ready: 'Patch klaar',
  testing: 'Testen',
  test_failed: 'Tests mislukt',
  ready_to_deploy: 'Klaar voor release',
  deploying: 'Release bezig',
  deploy_failed: 'Release mislukt',
  deployed: 'Uitgerold',
  rejected: 'Afgewezen',
};

function badge(status) {
  if (['deployed', 'ready_to_deploy'].includes(status)) return 'bg-green-100 text-green-700';
  if (['generation_failed', 'test_failed', 'deploy_failed'].includes(status)) return 'bg-red-100 text-red-700';
  if (['ready_for_operator_review', 'testing', 'patch_ready'].includes(status)) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
}

function shortSha(value) {
  return value ? String(value).slice(0, 10) : '—';
}

export default function LinkedInRepairMonitoringPanel() {
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await api.get('/operations/super-admin/linkedin-repairs/monitoring?limit=30');
      setData(response.data);
      setVisible(true);
      setError('');
    } catch (requestError) {
      if (requestError.response?.status === 403) {
        setVisible(false);
        return;
      }
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const repairs = useMemo(() => data?.proposals || [], [data]);
  const stuck = useMemo(() => [
    ...(data?.executions || []).filter((item) => item.stuck),
    ...(data?.sandboxJobs || []).filter((item) => item.stuck),
  ], [data]);
  const sandbox = data?.sandboxReadiness || {};
  const remoteRunner = data?.remoteRunner || {};
  const sandboxDeployment = data?.sandboxDeployment || {};
  const summary = data?.summary || {};

  if (!visible) return null;

  return (
    <section className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Self-healing LinkedIn</p>
          <h2 className="text-lg font-semibold text-gray-900">Repair pipeline</h2>
          <p className="text-sm text-gray-500">Structurele Puppeteer-incidenten, voorgestelde fixes, verificatie en read-only LinkedIn sandbox-tests.</p>
        </div>
        <button onClick={load} className="rounded-lg border bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Vernieuwen</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {sandboxDeployment.awaitingDifferentCommit && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong>Sandbox deployment nodig.</strong>{' '}
          De remote sandbox draait <code>{shortSha(sandboxDeployment.remoteCheckoutSha)}</code>, terwijl de eerstvolgende repair{' '}
          <code>{shortSha(sandboxDeployment.nextQueued?.commitSha)}</code> op branch <code>{sandboxDeployment.nextQueued?.branch || 'repair branch'}</code> verwacht. Deploy de sandbox op die repaircommit; de job blijft tot die tijd veilig queued.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Actieve repairs</span><strong className="mt-1 block text-xl">{summary.active || 0}</strong></div>
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Review nodig</span><strong className="mt-1 block text-xl text-amber-700">{summary.awaitingOperator || 0}</strong></div>
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Klaar voor release</span><strong className="mt-1 block text-xl text-green-700">{summary.readyToDeploy || 0}</strong></div>
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Mislukt</span><strong className="mt-1 block text-xl text-red-700">{summary.failed || 0}</strong></div>
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Vastgelopen jobs</span><strong className="mt-1 block text-xl text-red-700">{(summary.stuckExecutions || 0) + (summary.stuckSandboxJobs || 0)}</strong></div>
        <div className="rounded-lg border bg-white p-3"><span className="text-xs text-gray-500">Remote sandbox</span><strong className={`mt-1 block text-sm ${summary.sandboxReady ? 'text-green-700' : 'text-amber-700'}`}>{summary.sandboxReady ? 'Ready' : remoteRunner.reachable ? 'Niet ready' : 'Niet bereikbaar'}</strong></div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-xl border bg-white p-4 xl:col-span-2">
          <h3 className="font-semibold text-gray-900">Recente repair candidates</h3>
          <div className="mt-3 space-y-2 max-h-80 overflow-auto">
            {repairs.length === 0 && <p className="text-sm text-gray-500">Nog geen repair candidates.</p>}
            {repairs.map((item) => (
              <div key={item.id} className="rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><strong>{item.operation || 'LinkedIn actie'}</strong><span className="ml-2 text-xs text-gray-400">{item.errorCode || 'unknown'}</span></div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge(item.status)}`}>{labels[item.status] || item.status}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>Confidence: <strong>{item.confidence || '—'}</strong></span>
                  <span>{item.failureCount || 0} failures / {item.distinctAccounts || 0} accounts</span>
                  <span>PR: <strong>{item.pullRequestNumber ? `#${item.pullRequestNumber}` : '—'}</strong></span>
                  <span>SHA: <strong>{shortSha(item.commitSha)}</strong></span>
                  <span>Update: {item.ageMinutes == null ? '—' : `${item.ageMinutes} min geleden`}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-between gap-2"><h3 className="font-semibold text-gray-900">Remote sandbox runtime</h3><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${remoteRunner.ready ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{remoteRunner.ready ? 'Ready' : 'Niet ready'}</span></div>
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p>Bereikbaar: <strong>{remoteRunner.reachable ? 'ja' : 'nee'}</strong></p>
              <p>Database: <strong>{remoteRunner.databaseConnected ? 'verbonden' : 'niet ready'}</strong></p>
              <p>Deployed SHA: <strong><code>{shortSha(remoteRunner.checkoutSha)}</code></strong></p>
              <p>Matching queued jobs: <strong>{sandboxDeployment.matchingQueuedJobs || 0}</strong></p>
              <p>Laatste poll: <strong>{remoteRunner.lastPollAt ? new Date(remoteRunner.lastPollAt).toLocaleString() : '—'}</strong></p>
            </div>
            {(remoteRunner.blockers || []).length > 0 && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700"><strong className="block mb-1">Runtime blockers</strong>{remoteRunner.blockers.join(' · ')}</div>}
          </div>

          <div className="rounded-xl border bg-white p-4">
            <div className="flex items-center justify-between gap-2"><h3 className="font-semibold text-gray-900">Sandbox account setup</h3><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sandbox.ready ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{sandbox.ready ? 'Ready' : 'Niet ready'}</span></div>
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p>Runner-config: <strong>{sandbox.runnerEnabled ? 'aan' : 'uit'}</strong></p>
              <p>Dedicated user: <strong>{sandbox.dedicatedUser ? 'ja' : 'nee'}</strong></p>
              <p>Proxy match: <strong>{sandbox.proxyMatchesUser ? 'ja' : 'nee'}</strong></p>
              <p>LinkedIn session: <strong>{sandbox.credentials?.hasLinkedInSession ? 'beschikbaar' : 'ontbreekt'}</strong></p>
              <p>Cookiejar: <strong>{sandbox.credentials?.hasCookieJar ? 'beschikbaar' : 'ontbreekt'}</strong></p>
              <p>Sales fallback: <strong>{sandbox.credentials?.hasSalesFallback ? 'beschikbaar' : 'niet aanwezig'}</strong></p>
              <p>Encryptiesleutel: <strong>{sandbox.encryptionKeyConfigured ? 'ingesteld' : 'ontbreekt'}</strong></p>
            </div>
            {(sandbox.blockers || []).length > 0 && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700"><strong className="block mb-1">Account blockers</strong>{sandbox.blockers.join(' · ')}</div>}
          </div>
        </div>
      </div>

      {stuck.length > 0 && <div className="rounded-xl border border-red-200 bg-white p-4"><h3 className="font-semibold text-red-800">Aandacht nodig</h3><div className="mt-2 space-y-1 text-sm text-red-700">{stuck.map((item) => <p key={item.id}>{item.stuckReason} · {item.proposalId || item.id} · {item.ageMinutes ?? '—'} min</p>)}</div></div>}
    </section>
  );
}